import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SortBy, SortDirection, SortingOptions} from '../../types/SearchCriteria';

interface SortingOptionProps {
  value: SortBy;
  labelKey: string;
  isSelected: boolean;
  onSelect: (value: SortBy) => void;
}

const SortingOption: React.FC<SortingOptionProps> = ({value, labelKey, isSelected, onSelect}) => {
  const {t} = useTranslation();
  return (
    <div
      className={`px-3 py-1 rounded cursor-pointer text-sm hover:bg-gray-100 ${isSelected ? 'bg-gray-200 font-medium' : ''}`}
      onClick={() => onSelect(value)}
    >
      {t(labelKey)}
    </div>
  );
};

const SORT_OPTIONS: { value: SortBy; labelKey: string }[] = [
  {value: 'NAME', labelKey: 'gallery.filters.sortName'},
  {value: 'PLACE', labelKey: 'gallery.filters.sortPlace'},
  {value: 'DATE', labelKey: 'gallery.filters.sortDate'}
];

interface SortingFilterProps {
  sortingOptions?: SortingOptions;
  onFilterChange: (sortingOptions?: SortingOptions) => void;
}

const SortingFilter: React.FC<SortingFilterProps> = ({sortingOptions, onFilterChange}) => {
  const {t} = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [tempDirection, setTempDirection] = useState<SortDirection>(sortingOptions?.direction || 'ASC');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFieldSelect = (by: SortBy) => {
    const newBy = sortingOptions?.by === by ? 'RANDOM' : by;
    onFilterChange({by: newBy, direction: tempDirection});
    setIsDropdownOpen(false);
  };

  const handleDirectionToggle = () => {
    const newDirection: SortDirection = tempDirection === 'ASC' ? 'DESC' : 'ASC';
    setTempDirection(newDirection);
    onFilterChange({by: sortingOptions?.by || 'RANDOM', direction: newDirection});
  };

  const getDisplayLabel = () => {
    if (!sortingOptions?.by || sortingOptions.by === 'RANDOM') {
      return t('gallery.filters.sorting');
    }
    const option = SORT_OPTIONS.find(o => o.value === sortingOptions.by);
    const byLabel = option ? t(option.labelKey) : sortingOptions.by;
    return `${t('gallery.filters.sorting')}: ${byLabel}`;
  };

  const getDirectionIcon = () => tempDirection === 'ASC' ? 'fa-arrow-down-short-wide' : 'fa-arrow-down-wide-short';

  return (
    <div className="flex items-center space-x-2">
      <div className="relative" ref={dropdownRef}>
        <div
          className="flex items-center cursor-pointer hover:text-gray-600"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {sortingOptions?.by && sortingOptions.by !== 'RANDOM' && (
            <i className="mr-1 text-[0.5rem] fa fa-circle text-gray-600" title="Filter active"/>
          )}
          <span>{getDisplayLabel()}</span>
          <i className={`ml-1 text-xs fa fa-chevron-${isDropdownOpen ? 'up' : 'down'}`}/>
        </div>

        {isDropdownOpen && (
          <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 py-2 min-w-48">
            <div className="px-4">
              <div className="font-medium text-gray-700 mb-1">{t('gallery.filters.sortBy')}</div>
              <div className="space-y-1">
                {SORT_OPTIONS.map(option => (
                  <SortingOption
                    key={option.value}
                    value={option.value}
                    labelKey={option.labelKey}
                    isSelected={sortingOptions?.by === option.value}
                    onSelect={handleFieldSelect}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <i
        className={`fa ${getDirectionIcon()} cursor-pointer hover:text-gray-600`}
        onClick={handleDirectionToggle}
        title={tempDirection === 'ASC' ? t('gallery.filters.sortAscending') : t('gallery.filters.sortDescending')}
      />
    </div>
  );
};

export default SortingFilter;

